import {
  faInfoCircle,
  faPlus,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { useFieldArray, useFormContext, useFormState } from "react-hook-form";
import slugify from "slugify";

import { Input } from "src/base-components/Input";
import { RequiredAsterisk } from "src/base-components/RequiredAsterisk";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { getStepById } from "src/cases/templates/formSteps";
import {
  CaseTemplateFormValues,
  FileCategoryFormValue,
} from "src/cases/templates/formUtils/types";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { getFieldError } from "src/cases/templates/utils";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Icon, IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

export const ConfigureFileCategoriesStep: React.FC = () => {
  const step = getStepById("configure-file-categories");
  const form = useFormContext<CaseTemplateFormValues>();
  const { errors } = useFormState<CaseTemplateFormValues>();
  const { fields, remove, append } = useFieldArray({
    name: "attributes.file_categories",
  });

  const hasPermission = useCheckFieldPermission();

  const addCategory = () => {
    append(
      {
        key: "",
        display_name: "",
        description: "",
        _isNewlyAppended: true,
      } as FileCategoryFormValue,
      {
        focusName: `attributes.file_categories.${fields.length}.display_name`,
      },
    );
  };

  return (
    <FormStepWrapper
      description={step.description}
      docLink={step.docLink}
      title={step.title}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-8 gap-x-2 gap-y-3">
          <FileCategoryRow
            categoryKey={
              <div className="flex items-center gap-1 font-inter-normal-12px text-gray-500">
                Category key <RequiredAsterisk />
                <Tooltip
                  body="Internal name used to reference this category when adding files during case creation.
Use lowercase letters, numbers, and underscores only. Avoid spaces or special characters."
                  placement="top"
                  asChild
                >
                  <Icon icon={faInfoCircle} size="2xs" />
                </Tooltip>
              </div>
            }
            categoryName={
              <div className="font-inter-normal-12px text-gray-500">
                Category name <RequiredAsterisk />
              </div>
            }
            description={
              <div className="font-inter-normal-12px text-gray-500">
                Description
              </div>
            }
          />
          {fields.map((field, index) => {
            const isDefault = isDefaultCategory(
              field as unknown as FileCategoryFormValue,
            );
            const isKeyFieldDisabled =
              isDefault ||
              !hasPermission("edit", `attributes.file_categories.${index}.key`);
            const isNameFieldDisabled =
              isDefault ||
              !hasPermission(
                "edit",
                `attributes.file_categories.${index}.display_name`,
              );
            const isDescriptionFieldDisabled =
              isDefault ||
              !hasPermission(
                "edit",
                `attributes.file_categories.${index}.description`,
              );

            return (
              <FileCategoryRow
                key={field.id}
                categoryKey={
                  <div className="relative">
                    <Input
                      data-loc={`file-category-${index}-key`}
                      placeholder="e.g., category_name"
                      monospaced
                      {...form.register(
                        `attributes.file_categories.${index}.key`,
                      )}
                      disabled={isKeyFieldDisabled}
                      errored={
                        !!getFieldError(
                          errors,
                          `attributes.file_categories.${index}.key`,
                        )
                      }
                      fullWidth
                    />
                    {getFieldError(
                      errors,
                      `attributes.file_categories.${index}.key`,
                    ) && (
                      <ErrorHint>
                        {
                          getFieldError(
                            errors,
                            `attributes.file_categories.${index}.key`,
                          )?.message
                        }
                      </ErrorHint>
                    )}
                  </div>
                }
                categoryName={
                  <div className="relative">
                    <Input
                      data-loc={`file-category-${index}-name`}
                      placeholder="Category name"
                      {...form.register(
                        `attributes.file_categories.${index}.display_name`,
                        {
                          onChange: (e) => {
                            const { isDirty } = form.getFieldState(
                              `attributes.file_categories.${index}.key`,
                            );

                            if (!isDirty && !isDefault && !isKeyFieldDisabled) {
                              const slug = slugify(e.target.value, {
                                lower: true,
                                strict: true,
                                replacement: "_",
                              });
                              form.setValue(
                                `attributes.file_categories.${index}.key`,
                                slug,
                              );
                            }
                          },
                          deps: `attributes.file_categories.${index}.key`,
                        },
                      )}
                      disabled={isNameFieldDisabled}
                      errored={
                        !!getFieldError(
                          errors,
                          `attributes.file_categories.${index}.display_name`,
                        )
                      }
                      fullWidth
                    />
                    {getFieldError(
                      errors,
                      `attributes.file_categories.${index}.display_name`,
                    ) && (
                      <ErrorHint>
                        {
                          getFieldError(
                            errors,
                            `attributes.file_categories.${index}.display_name`,
                          )?.message
                        }
                      </ErrorHint>
                    )}
                  </div>
                }
                description={
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Input
                        data-loc={`file-category-${index}-description`}
                        placeholder="Add a description for this category"
                        {...form.register(
                          `attributes.file_categories.${index}.description`,
                        )}
                        disabled={isDescriptionFieldDisabled}
                        errored={
                          !!getFieldError(
                            errors,
                            `attributes.file_categories.${index}.description`,
                          )
                        }
                        fullWidth
                      />
                      {getFieldError(
                        errors,
                        `attributes.file_categories.${index}.description`,
                      ) && (
                        <ErrorHint>
                          {
                            getFieldError(
                              errors,
                              `attributes.file_categories.${index}.description`,
                            )?.message
                          }
                        </ErrorHint>
                      )}
                    </div>
                    {!isDefault &&
                      hasPermission("delete", `attributes.file_categories`) && (
                        <IconButton
                          dataLoc={`file-category-${index}-remove`}
                          icon={faTrashCan}
                          size="sm"
                          onClick={() => remove(index)}
                        />
                      )}
                  </div>
                }
              />
            );
          })}
        </div>
        {hasPermission("add", "attributes.file_categories") && (
          <div className="mt-4">
            <Button
              dataLoc="add-file-category-button"
              iconLeft={faPlus}
              size="sm"
              variant="secondary"
              onClick={addCategory}
            >
              Add a file category
            </Button>
          </div>
        )}
      </div>
    </FormStepWrapper>
  );
};

const FileCategoryRow: React.FC<{
  categoryName: React.ReactNode;
  categoryKey: React.ReactNode;
  description: React.ReactNode;
}> = ({ categoryName, categoryKey, description }) => {
  return (
    <>
      <div className="col-span-2">{categoryName}</div>
      <div className="col-span-2">{categoryKey}</div>
      <div className="col-span-4">{description}</div>
    </>
  );
};

const isDefaultCategory = (category: FileCategoryFormValue): boolean => {
  return !!category._isDefault;
};
